import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, MapPin, Calendar, Shield, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { listingUrl } from "@/lib/slug";

export type CarouselListing = {
  id: string;
  title: string;
  brand: string;
  model: string;
  price: number;
  city: string;
  images: string[] | null;
  condition: string;
  dgt_approved: boolean | null;
  created_at: string;
  distanceKm?: number;
};

type Props = {
  title: string;
  subtitle?: string;
  listings: CarouselListing[];
  loading?: boolean;
  monthlyRate?: number;
  emptyHint?: string;
  viewAllHref?: string;
  showViewAll?: boolean;
  autoplay?: boolean;
  autoplayMs?: number;
};

const ListingsCarousel = ({
  title,
  subtitle,
  listings,
  loading = false,
  monthlyRate = 0.04,
  emptyHint = "Aún no hay anuncios para mostrar.",
  viewAllHref = "/buscar",
  showViewAll = true,
  autoplay = false,
  autoplayMs = 4000,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [paused, setPaused] = useState(false);
  const [pageInfo, setPageInfo] = useState({ pages: 1, current: 0 });
  const { user } = useAuth();

  // Drag state (mouse + touch)
  const dragState = useRef({ down: false, startX: 0, startScroll: 0, moved: false });

  // Autoplay
  useEffect(() => {
    if (!autoplay || paused || listings.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const id = window.setInterval(() => {
      if (!el) return;
      const cardWidth = (el.firstElementChild as HTMLElement)?.clientWidth || 280;
      const step = cardWidth + 16;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + step, behavior: "smooth" });
    }, autoplayMs);
    return () => window.clearInterval(id);
  }, [autoplay, autoplayMs, paused, listings.length]);

  // Track pagination indicators
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const pages = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
      const current = Math.min(pages - 1, Math.round(el.scrollLeft / el.clientWidth));
      setPageInfo({ pages, current });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [listings.length]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("favorites")
      .select("listing_id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (data) setFavorites(new Set(data.map((f: any) => f.listing_id)));
      });
  }, [user]);

  const toggleFav = async (e: React.MouseEvent, listingId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Inicia sesión para guardar favoritos");
      return;
    }
    if (favorites.has(listingId)) {
      await supabase.from("favorites").delete().eq("user_id", user.id).eq("listing_id", listingId);
      setFavorites((f) => {
        const n = new Set(f);
        n.delete(listingId);
        return n;
      });
    } else {
      await supabase.from("favorites").insert({ user_id: user.id, listing_id: listingId });
      setFavorites((f) => new Set(f).add(listingId));
      toast.success("Añadido a favoritos");
    }
  };

  const scroll = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = (el.firstElementChild as HTMLElement)?.clientWidth || 280;
    el.scrollBy({ left: dir * (cardWidth + 16) * 2, behavior: "smooth" });
  };

  const scrollToPage = (page: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: page * el.clientWidth, behavior: "smooth" });
  };

  // Mouse drag
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { down: true, startX: e.pageX, startScroll: el.scrollLeft, moved: false };
    el.classList.add("cursor-grabbing");
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.down) return;
    const dx = e.pageX - dragState.current.startX;
    if (Math.abs(dx) > 5) dragState.current.moved = true;
    el.scrollLeft = dragState.current.startScroll - dx;
  };
  const endDrag = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.classList.remove("cursor-grabbing");
    dragState.current.down = false;
  };
  const onClickCapture = (e: React.MouseEvent) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
    }
  };

  // Touch (pause autoplay while interacting; rely on native scroll)
  const onTouchStart = () => setPaused(true);
  const onTouchEnd = () => {
    // small delay to avoid resuming during momentum scrolling
    window.setTimeout(() => setPaused(false), 800);
  };

  return (
    <section className="border-t border-border bg-background py-14">
      <div className="container">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {!loading && listings.length > 0 && (
            <div className="hidden items-center gap-2 md:flex">
              <Button variant="outline" size="icon" className="h-10 w-10 rounded-full" onClick={() => scroll(-1)} aria-label="Anterior">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-10 w-10 rounded-full" onClick={() => scroll(1)} aria-label="Siguiente">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-14 text-center">
            <PackageOpen className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{emptyHint}</p>
            <Button asChild variant="outline" size="sm" className="mt-2">
              <Link to="/publicar">Publicar el primero</Link>
            </Button>
          </div>
        ) : (
          <div
            ref={scrollRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={() => { endDrag(); setPaused(false); }}
            onMouseEnter={() => setPaused(true)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onClickCapture={onClickCapture}
            className="-mx-4 flex cursor-grab snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 select-none [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
          >
            {listings.map((l) => {
              const img = l.images?.[0];
              const monthly = Math.round(Number(l.price) * monthlyRate);
              const isFav = favorites.has(l.id);
              return (
                <Link
                  key={l.id}
                  to={listingUrl(l)}
                  draggable={false}
                  className="group relative w-[260px] shrink-0 snap-start overflow-hidden rounded-xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 sm:w-[280px]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    {img ? (
                      <img
                        src={img}
                        alt={l.title}
                        loading="lazy"
                        draggable={false}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground">Sin imagen</div>
                    )}
                    <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground shadow-sm backdrop-blur-sm">
                      {l.brand}
                    </div>
                    {l.dgt_approved && (
                      <div className="absolute left-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm backdrop-blur-sm">
                        <Shield className="h-3 w-3" /> DGT
                      </div>
                    )}
                    <button
                      onClick={(e) => toggleFav(e, l.id)}
                      className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-background"
                      aria-label="Favorito"
                    >
                      <Heart className={cn("h-4 w-4", isFav && "fill-destructive text-destructive")} />
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="line-clamp-1 font-display text-sm font-bold uppercase tracking-tight">{l.title}</h3>
                    <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="capitalize">{l.condition}</span>
                      <span className="opacity-50">|</span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(l.created_at).toLocaleDateString("es-ES", { month: "short", year: "numeric" })}
                      </span>
                      <span className="opacity-50">|</span>
                      <span className="inline-flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{l.city}</span>
                      </span>
                    </p>
                    <div className="mt-3 flex items-end justify-between">
                      <span className="font-display text-lg font-bold text-foreground">
                        {Number(l.price).toLocaleString("es-ES")} €
                      </span>
                      {monthly > 0 && (
                        <span className="text-sm font-semibold text-primary">{monthly} €/mes</span>
                      )}
                    </div>
                    {typeof l.distanceKm === "number" && (
                      <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        <MapPin className="h-3 w-3" />
                        a {l.distanceKm < 1 ? "<1" : Math.round(l.distanceKm)} km de ti
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && listings.length > 0 && pageInfo.pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {Array.from({ length: pageInfo.pages }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToPage(i)}
                aria-label={`Ir a página ${i + 1}`}
                className={cn(
                  "h-2 rounded-full transition-all relative before:absolute before:inset-0 before:-m-3 before:content-['']",
                  i === pageInfo.current ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60"
                )}
              />
            ))}
          </div>
        )}

        {showViewAll && listings.length > 0 && (
          <div className="mt-8 flex justify-center">
            <Button asChild size="lg" className="rounded-full bg-gradient-to-r from-primary to-primary/85 px-8 font-semibold shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30">
              <Link to={viewAllHref}>Ver todos los patinetes</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ListingsCarousel;
